// Specs tab. Left = feature list (status pill per feature). Right =
// markdown editor + side preview for the selected feature, with AI
// generate + save buttons.

import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles, Save, RotateCcw, FileText, AlertTriangle, CheckCircle2, FolderInput } from 'lucide-react';
import type {
  DiscoveryFeature, MigrationUnit, FeatureSpecsPayload, FeatureSpec,
} from '../hooks/sessionTypes';

interface Props {
  mu: MigrationUnit;
  features: DiscoveryFeature[];
  specsPayload: FeatureSpecsPayload;
  generating: Record<string, boolean>;
  saving: Record<string, boolean>;
  generatingAll: boolean;
  onGenerate: (featureId: string, opts?: { force?: boolean }) => Promise<FeatureSpec | null>;
  onSave: (featureId: string, markdown: string) => Promise<FeatureSpec | null>;
  onGenerateAll: (
    featureIds: string[],
    onProgress?: (done: number, total: number) => void,
  ) => Promise<void>;
  onOpenFolderImport: () => void;
}

type SpecStatus = 'none' | 'fresh' | 'user-edited' | 'stale';

const MigrationUnitSpecs: React.FC<Props> = ({
  mu, features, specsPayload, generating, saving, generatingAll, onGenerate, onSave, onGenerateAll, onOpenFolderImport,
}) => {
  const [selected, setSelected] = useState<string | null>(features[0]?.candidateId ?? null);
  const [draft, setDraft] = useState<string>('');
  const [dirty, setDirty] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);

  // Keep selection valid when the feature set changes.
  useEffect(() => {
    if (selected && features.some((f) => f.candidateId === selected)) return;
    setSelected(features[0]?.candidateId ?? null);
  }, [features, selected]);

  // Pull the selected spec's markdown into the editor when selection
  // changes or the underlying spec record updates externally.
  const selectedSpec = selected ? specsPayload.specs[selected] : undefined;
  useEffect(() => {
    setDraft(selectedSpec?.markdown ?? '');
    setDirty(false);
  }, [selectedSpec?.markdown, selectedSpec?.savedAt, selectedSpec?.generatedAt, selected]);

  const handleGenerate = async (force = false) => {
    if (!selected) return;
    const spec = await onGenerate(selected, { force });
    if (spec) {
      setDraft(spec.markdown);
      setDirty(false);
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    const spec = await onSave(selected, draft);
    if (spec) setDirty(false);
  };

  const handleGenerateAll = async () => {
    const missing = features
      .filter((f) => !specsPayload.specs[f.candidateId]?.markdown)
      .map((f) => f.candidateId);
    if (missing.length === 0) {
      if (!window.confirm('All specs already exist. Re-generate everything? Manual edits will NOT be overwritten.')) return;
    }
    const targets = missing.length > 0 ? missing : features.map((f) => f.candidateId);
    setBatchProgress({ done: 0, total: targets.length });
    await onGenerateAll(targets, (done, total) => setBatchProgress({ done, total }));
    setBatchProgress(null);
  };

  const statusFor = (id: string): SpecStatus => {
    const spec = specsPayload.specs[id];
    if (!spec?.markdown) return 'none';
    if (spec.userEdited) return 'user-edited';
    if (mu.flowGeneratedAt && spec.generatedFromFlowAt && spec.generatedFromFlowAt !== mu.flowGeneratedAt) return 'stale';
    return 'fresh';
  };

  const counts = useMemo(() => {
    const acc = { none: 0, fresh: 0, 'user-edited': 0, stale: 0 } as Record<SpecStatus, number>;
    for (const f of features) acc[statusFor(f.candidateId)] += 1;
    return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, specsPayload, mu.flowGeneratedAt]);

  const selectedFeature = features.find((f) => f.candidateId === selected);
  const isGenerating = !!selected && !!generating[selected];
  const isSaving = !!selected && !!saving[selected];

  return (
    <div className="mu-tab-body mu-specs">
      <aside className="mu-specs-left">
        <header className="mu-specs-list-head">
          <h2>Features</h2>
          <div className="mu-specs-list-actions">
            <button
              type="button"
              className="dv-btn dv-btn--ghost"
              onClick={onOpenFolderImport}
              disabled={features.length === 0}
              title="Bulk-import specs from a folder of markdown files"
            >
              <FolderInput size={12} /> Import folder
            </button>
            <button
              type="button"
              className="dv-btn dv-btn--primary"
              onClick={handleGenerateAll}
              disabled={generatingAll || features.length === 0}
              title="Generate specs for every feature in this MU"
            >
              {generatingAll
                ? batchProgress
                  ? `${batchProgress.done}/${batchProgress.total}…`
                  : 'Generating…'
                : <><Sparkles size={12} /> Generate all</>}
            </button>
          </div>
        </header>
        <div className="mu-specs-list-stats">
          <span title="Generated">✓ {counts.fresh}</span>
          <span title="User-edited">✎ {counts['user-edited']}</span>
          <span title="Stale (flow changed)">⚠ {counts.stale}</span>
          <span title="Not generated yet">○ {counts.none}</span>
        </div>
        <ul className="mu-specs-list">
          {features.map((f) => {
            const status = statusFor(f.candidateId);
            return (
              <li key={f.candidateId}>
                <button
                  type="button"
                  className={`mu-specs-item ${selected === f.candidateId ? 'mu-specs-item--selected' : ''}`}
                  onClick={() => setSelected(f.candidateId)}
                >
                  <SpecStatusIcon status={status} />
                  <span className="mu-specs-item-name">{f.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      <section className="mu-specs-right">
        {!selectedFeature ? (
          <div className="mu-specs-empty">Select a feature to view its spec.</div>
        ) : (
          <>
            <header className="mu-specs-editor-head">
              <div className="mu-specs-editor-title">
                <FileText size={13} />
                <h3>{selectedFeature.name}</h3>
                <StatusPill status={statusFor(selectedFeature.candidateId)} />
              </div>
              <div className="mu-specs-editor-actions">
                {selectedSpec?.userEdited && (
                  <button
                    type="button"
                    className="dv-btn dv-btn--ghost"
                    onClick={() => {
                      if (window.confirm('Discard manual edits and re-generate this spec from the AI?')) handleGenerate(true);
                    }}
                    disabled={isGenerating}
                  >
                    {isGenerating ? <span className="dv-spinner" /> : <RotateCcw size={13} />}
                    Regenerate (overwrite)
                  </button>
                )}
                {!selectedSpec?.userEdited && (
                  <button
                    type="button"
                    className="dv-btn dv-btn--ghost"
                    onClick={() => handleGenerate(false)}
                    disabled={isGenerating}
                  >
                    {isGenerating ? <span className="dv-spinner" /> : <Sparkles size={13} />}
                    {selectedSpec?.markdown ? 'Regenerate' : 'Generate spec'}
                  </button>
                )}
                <button
                  type="button"
                  className="dv-btn dv-btn--primary"
                  onClick={handleSave}
                  disabled={!dirty || isSaving}
                  title="Save manual edits"
                >
                  {isSaving ? <span className="dv-spinner" /> : <Save size={13} />}
                  Save
                </button>
              </div>
            </header>

            <div className="mu-specs-editor-body">
              <textarea
                className="mu-specs-editor"
                value={draft}
                onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
                placeholder="Generate a draft, or write the markdown spec here."
                spellCheck={false}
              />
              <div className="mu-specs-preview">
                {draft.trim() ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft}</ReactMarkdown>
                ) : (
                  <p className="mu-prose-empty">Preview appears here.</p>
                )}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default MigrationUnitSpecs;

const SpecStatusIcon: React.FC<{ status: SpecStatus }> = ({ status }) => {
  if (status === 'fresh') return <CheckCircle2 size={12} className="mu-spec-pip mu-spec-pip--ok" />;
  if (status === 'user-edited') return <span className="mu-spec-pip mu-spec-pip--edit">✎</span>;
  if (status === 'stale') return <AlertTriangle size={12} className="mu-spec-pip mu-spec-pip--stale" />;
  return <span className="mu-spec-pip mu-spec-pip--none">○</span>;
};

const StatusPill: React.FC<{ status: SpecStatus }> = ({ status }) => {
  if (status === 'fresh') return <span className="mu-status-pill mu-status-pill--ok">generated</span>;
  if (status === 'user-edited') return <span className="mu-status-pill mu-status-pill--edit">user-edited</span>;
  if (status === 'stale') return <span className="mu-status-pill mu-status-pill--stale">flow has changed since draft</span>;
  return <span className="mu-status-pill">not generated yet</span>;
};
